<template>
  <div>
    <b-container fluid>
      <h3 class="mt-3 mb-3">Committed Stock Discrepancy</h3>
      <p class="text-muted">
        Products where the physical "Committed" WMS stock doesn't match the outstanding order commitment.
        Positive gap = stock sitting in the Committed bucket with no (or less) matching order commitment,
        usually caused by an order being fulfiled without the stock-out step completing.
      </p>

      <div class="search_bar mb-1">
        <b-card>
          <b-row class="mb-1">
            <b-col cols="3" class="pr-1">
              <b-form-select
                size="sm"
                v-model="search_criteria.client_selected"
                :options="client_options"
                @change="onSearch"
              />
            </b-col>
            <b-col cols="3" class="pl-0 pr-1">
              <b-form-input
                id="input-small"
                size="sm"
                type="text"
                placeholder="Product SKU / Channel SKU / Description"
                v-model="search_criteria.keyword"
               @keyup.enter="onSearch"></b-form-input>
            </b-col>
            <b-col class="text-left p-0">
              <b-button
                class="btn-action mr-1"
                size="sm"
                variant="info"
                @click="onSearch"
              >
                <font-awesome-icon class="mr-1" icon="search" />Search
              </b-button>
              <b-button
                class="btn-action"
                size="sm"
                variant="success"
                @click="exportCSV"
              >
                <font-awesome-icon class="mr-1" icon="file-export" />Export
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </div>

      <b-row class="mb-3">
        <b-col cols="3">
          <b-card class="text-center" bg-variant="light">
            <div class="summary_label">Affected Products</div>
            <div class="summary_value">{{ number_formatter(summary.total_products) }}</div>
          </b-card>
        </b-col>
        <b-col cols="3">
          <b-card class="text-center" bg-variant="light">
            <div class="summary_label">Clients Affected</div>
            <div class="summary_value">{{ number_formatter(summary.total_clients) }}</div>
          </b-card>
        </b-col>
        <b-col cols="3">
          <b-card class="text-center" bg-variant="light">
            <div class="summary_label">Stuck Committed Qty</div>
            <div class="summary_value text-danger">{{ number_formatter(summary.total_overcommitted_qty) }}</div>
          </b-card>
        </b-col>
        <b-col cols="3">
          <b-card class="text-center" bg-variant="light">
            <div class="summary_label">Missing Committed Qty</div>
            <div class="summary_value text-warning">{{ number_formatter(summary.total_undercommitted_qty) }}</div>
          </b-card>
        </b-col>
      </b-row>

      <strong># of Records: {{ total_rows }}</strong>

      <b-row class="mb-1">
        <b-col cols="1" class="pr-0">
          <b-form-select
            size="sm"
            v-model="sel_table_limit"
            :options="[50, 100, 200, 500]"
            @change="getDiscrepancies()"
          />
        </b-col>
        <b-col class="pl-0 pr-1">
          <b-pagination
            size="sm"
            :total-rows="total_rows"
            v-model="currentPage"
            :per-page="sel_table_limit"
            @change="onPageChange"
            :limit="PAGING_LIMIT"
          ></b-pagination>
        </b-col>
      </b-row>

      <b-table
        responsive
        small
        outlined
        striped
        hover
        selectable
        select-mode="single"
        :items="items"
        :fields="fields"
        @row-clicked="onRowClick"
      >
        <template v-slot:cell(wms_committed_qty)="data">
          <div class="text-right">{{ number_formatter(data.item.wms_committed_qty) }}</div>
        </template>
        <template v-slot:cell(order_committed_qty)="data">
          <div class="text-right">{{ number_formatter(data.item.order_committed_qty) }}</div>
        </template>
        <template v-slot:cell(gap_qty)="data">
          <div
            class="text-right"
            :class="data.item.gap_qty > 0 ? 'text-danger' : 'text-warning'"
          >{{ number_formatter(data.item.gap_qty) }}</div>
        </template>
        <template v-slot:cell(locations)="data">
          {{ data.item.locations || "-" }}
        </template>
      </b-table>
      <p class="text-muted"><small>Click a row to see the orders behind that SKU's gap.</small></p>

      <b-pagination
        size="sm"
        :total-rows="total_rows"
        v-model="currentPage"
        :per-page="sel_table_limit"
        @change="onPageChange"
        :limit="PAGING_LIMIT"
      ></b-pagination>

      <b-modal
        v-model="show_detail_modal"
        size="xl"
        :title="detail_title"
        hide-footer
      >
        <p class="text-muted">
          Orders already marked "fulfiled" with committed stock for this SKU, but no matching "Stock Out"
          transaction was ever logged against the Committed bucket for that order. These are the likely
          source orders behind this SKU's gap.
          <code>committed_record_count = 0</code> means the commit record was never created for that order
          (nothing to stock out from); a non-zero value means the record existed but the stock-out step was
          skipped or failed.
        </p>

        <b-row class="mb-3">
          <b-col cols="6">
            <b-card class="text-center" bg-variant="light">
              <div class="summary_label">Suspect Orders</div>
              <div class="summary_value">{{ number_formatter(suspect_orders_summary.total_orders) }}</div>
            </b-card>
          </b-col>
          <b-col cols="6">
            <b-card class="text-center" bg-variant="light">
              <div class="summary_label">Committed Qty Stuck</div>
              <div class="summary_value text-danger">{{ number_formatter(suspect_orders_summary.total_committed_qty_stuck) }}</div>
            </b-card>
          </b-col>
        </b-row>

        <b-table
          responsive
          small
          outlined
          striped
          hover
          :items="suspect_orders"
          :fields="suspect_order_fields"
          :busy="detail_loading"
        >
          <template v-slot:cell(fulfil_at)="data">
            {{ displayDate(data.item.fulfil_at) }}
          </template>
          <template v-slot:cell(committed_qty_total)="data">
            <div class="text-right">{{ number_formatter(data.item.committed_qty_total) }}</div>
          </template>
          <template v-slot:cell(committed_sku_count)="data">
            <div class="text-right">{{ number_formatter(data.item.committed_sku_count) }}</div>
          </template>
          <template v-slot:cell(committed_record_count)="data">
            <span :class="data.item.committed_record_count == 0 ? 'text-warning' : 'text-danger'">
              {{ number_formatter(data.item.committed_record_count) }}
            </span>
          </template>
          <template v-slot:table-busy>
            <div class="text-center my-2">Loading...</div>
          </template>
          <template v-slot:empty>
            <div class="text-center my-2">No suspect orders found for this SKU.</div>
          </template>
        </b-table>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import moment from "moment";
import apiService from "../services/ApiService";

const fields = [
  {
    key: "client",
    label: "Client"
  },
  {
    key: "product_sku",
    label: "Product SKU"
  },
  {
    key: "channel_sku",
    label: "Channel SKU"
  },
  {
    key: "other_sku",
    label: "Other SKU"
  },
  {
    key: "product_description",
    label: "Description"
  },
  {
    key: "wms_committed_qty",
    label: "WMS Committed Qty",
    class: "text-right"
  },
  {
    key: "order_committed_qty",
    label: "Order Committed Qty",
    class: "text-right"
  },
  {
    key: "gap_qty",
    label: "Gap",
    class: "text-right"
  },
  {
    key: "locations",
    label: "Location(s)"
  }
];

const suspect_order_fields = [
  {
    key: "order_number",
    label: "Order Number"
  },
  {
    key: "client_name",
    label: "Client"
  },
  {
    key: "channel",
    label: "Channel"
  },
  {
    key: "fulfil_at",
    label: "Fulfiled At"
  },
  {
    key: "committed_sku_count",
    label: "SKU Count",
    class: "text-right"
  },
  {
    key: "committed_qty_total",
    label: "Committed Qty",
    class: "text-right"
  },
  {
    key: "committed_record_count",
    label: "Commit Records Found",
    class: "text-right"
  }
];

export default {
  name: "CommittedStockDiscrepancy",
  inject: ["API_HOST", "TABLE_LIMIT", "PAGING_LIMIT"],
  mixins: [apiService],
  data() {
    return {
      sel_table_limit: this.TABLE_LIMIT,
      items: [],
      fields: fields,
      currentPage: 1,
      table_offset: 0,
      total_rows: 0,
      summary: {
        total_products: 0,
        total_clients: 0,
        total_overcommitted_qty: 0,
        total_undercommitted_qty: 0
      },
      suspect_orders: [],
      suspect_order_fields: suspect_order_fields,
      suspect_orders_summary: {
        total_orders: 0,
        total_committed_qty_stuck: 0
      },
      show_detail_modal: false,
      detail_loading: false,
      detail_title: "",
      search_criteria: {
        keyword: "",
        client_selected: null
      },
      client_options: [
        {
          value: null,
          text: "All Client"
        }
      ]
    };
  },
  async created() {
    await this.getDiscrepancies();
  },
  methods: {
    number_formatter(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return Number(value).toLocaleString();
    },

    async getDiscrepancies() {
      this.loader = this.$loading.show({
        isFullPage: true,
        canCancel: false
      });

      let response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "committed_stock_discrepancy_get.php",
        data: {
          offset: this.table_offset,
          limit: this.sel_table_limit,
          client_id: this.search_criteria.client_selected,
          search_criteria: this.search_criteria
        }
      });

      this.items = response.data.discrepancies;
      this.total_rows = response.data.total_rows;
      this.summary = response.data.summary;

      this.client_options = [{ value: null, text: "All Client" }];
      for (let i in response.data.affected_clients) {
        this.client_options.push({
          value: response.data.affected_clients[i].id,
          text: response.data.affected_clients[i].name
        });
      }

      this.loader.hide();
    },

    async onRowClick(row) {
      this.detail_title = `${row.client} - ${row.product_sku} (gap: ${this.number_formatter(row.gap_qty)})`;
      this.suspect_orders = [];
      this.suspect_orders_summary = {
        total_orders: 0,
        total_committed_qty_stuck: 0
      };
      this.show_detail_modal = true;
      this.detail_loading = true;

      let response = await this.$http({
        method: "POST",
        baseURL: `${this.API_HOST}`,
        url: "committed_stock_discrepancy_get.php",
        data: {
          client_id: row.client_id,
          product_sku: row.product_sku
        }
      });

      this.suspect_orders = response.data.suspect_orders || [];
      this.suspect_orders_summary = response.data.suspect_orders_summary || {
        total_orders: 0,
        total_committed_qty_stuck: 0
      };
      this.detail_loading = false;
    },

    displayDate(d) {
      if (d) {
        return moment(d).format("YYYY-MM-DD HH:mm");
      }
      return "-";
    },

    onSearch() {
      for (const key in this.search_criteria) {
        if (typeof this.search_criteria[key] === "string") {
          this.search_criteria[key] = this.search_criteria[key].trim();
        }
      }
      this.table_offset = 0;
      this.currentPage = 1;
      this.getDiscrepancies();
    },

    exportCSV() {
      let export_url = `${this.API_HOST}/committed_stock_discrepancy_export.php?`;
      if (this.search_criteria.client_selected) {
        export_url += `client_id=${this.search_criteria.client_selected}&`;
      }
      if (this.search_criteria.keyword) {
        export_url += `keyword=${encodeURIComponent(this.search_criteria.keyword)}&`;
      }
      window.open(export_url, "_blank");
    },

    onPageChange(page_to_change) {
      this.table_offset = (page_to_change - 1) * this.sel_table_limit;
      this.getDiscrepancies();
    }
  }
};
</script>

<style scoped>
.summary_label {
  font-size: 0.85rem;
  color: #6c757d;
}
.summary_value {
  font-size: 1.6rem;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/components/CommittedStockDiscrepancy.vue